import React from "react"
import { useHistory } from "react-router-dom"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import * as eventsApi from "api/events"
import { updateEventGroup, updateGuest } from "context/actions"
import {
  EventGroup, Guest, AccountUser, Notification, User, SubscriptionType,
} from "sharedTypes"
import {
  Text, Box, VStack,
} from "@chakra-ui/react"
import GuestListIcon from "components/elements/GuestListIcon"
import GuestAvatar from "components/elements/GuestAvatar"
import UpgradeAlert from "components/elements/UpgradeAlert"
import { useCurrentEvent } from "queries/events"
import { useQueryClient } from "react-query"
import useCurrentAccount from "services/useCurrentAccount"
import useCurrentPlanType from "utilities/useCurrentPlanType"
import { putGuest } from "api/guests"
import { eventAndAccountAdmins } from "utilities/objects"
import { useEventGuestPaths } from "utilities/routes"
import { Control, useController } from "react-hook-form"
import UserAlertRow, { AlertColumnHeaders } from "./UserListItem"

export type AlertRow = { userId: string; email: boolean; sms: boolean }

type Props = {
  teamMembers: AccountUser[]
  guest?: Guest
  guestList?: EventGroup
  onClose?: () => void
}

const buildInitialNotifications = (
  eligibleUserIds: string[],
  existing: Notification[],
): AlertRow[] => eligibleUserIds.map((userId) => {
  const match = existing.find((n) => n.userId === userId)

  return {
    userId,
    email: match?.email ?? false,
    sms: match?.sms ?? false,
  }
})

export const toApiNotifications = (rows: AlertRow[]) => rows
  .filter((r) => r.email || r.sms)
  .map(({ userId, email, sms }) => ({ userId, email, sms }))

const CheckInAlert = ({
  teamMembers, guest, guestList, onClose,
}: Props) => {
  const queryClient = useQueryClient()
  const { subscription } = useCurrentAccount()
  const { onLargestPlan } = useCurrentPlanType()
  const { data: currentEvent } = useCurrentEvent()
  const history = useHistory()
  const { eventGuestPath } = useEventGuestPaths()
  const { users = [] } = currentEvent || {}

  const allUsers = eventAndAccountAdmins(users, teamMembers)
  const eligibleUsers = allUsers
    .filter((u) => u.user?.confirmed)
    .map(({ user }) => user)

  let description: React.ReactNode
  let previewBox: React.ReactNode
  let handleClose: () => void = onClose!
  let existingNotifications: Notification[] = []
  let onSubmit: (values: { notifications: AlertRow[] }) => Promise<void> = async () => {}

  if (guestList) {
    handleClose = onClose!
    description = <>anyone on the <Text as="strong" fontWeight="bold">&ldquo;{guestList.title}&rdquo;</Text> guest list checks in</>
    previewBox = (
      <Box borderWidth={1} borderRadius="md" borderColor="gray.200" bg="gray.50" py={3} px={4} display="flex" alignItems="center" gap={4} mb={6}>
        <Box
          borderWidth={1}
          borderRadius="md"
          borderColor={guestList.color ?? "gray.200"}
          bg={`color-mix(in srgb, ${guestList.color ?? "#9E9E9E"} 8%, white)`}
          p={2}
          flexShrink={0}
        >
          <GuestListIcon icon={guestList.icon} color={guestList.color} boxSize={6} />
        </Box>
        <Text fontWeight="semibold" fontSize="lg">{guestList.title}</Text>
      </Box>
    )
    existingNotifications = guestList.notifications || []
    onSubmit = ({ notifications }) => eventsApi.putEventGroup(
      currentEvent.id,
      { ...guestList, notifications: toApiNotifications(notifications) as any },
    ).then(({ data }) => {
      updateEventGroup(queryClient, currentEvent.id, data)
      handleClose()
    })
  } else if (guest) {
    const {
      firstName, lastName, email, photoUrl,
    } = guest.personInfo
    handleClose = () => history.push(eventGuestPath(currentEvent.id, guest.id))
    description = <>this guest checks in</>
    previewBox = (
      <Box borderWidth={1} borderRadius="md" borderColor="gray.200" bg="gray.50" py={4} px={4} display="flex" alignItems="center" gap={4} mb={6}>
        <Box flexShrink={0}>
          <GuestAvatar
            firstName={firstName}
            lastName={lastName}
            photoUrl={photoUrl}
            status={guest.status}
            checkedIn={guest.checkedIn > 0}
            size={16}
          />
        </Box>
        <Box>
          <Text fontWeight="semibold" fontSize="lg">{firstName} {lastName}</Text>
          <Text fontSize="sm" color="gray.700">{email}</Text>
        </Box>
      </Box>
    )
    existingNotifications = guest.notifications || []
    onSubmit = ({ notifications }) => putGuest(
      currentEvent.id,
      guest.id,
      { ...guest, notifications: toApiNotifications(notifications) as any },
    ).then(({ data }) => {
      updateGuest(queryClient, data)
      handleClose()
    })
  }

  const initialValues = {
    notifications: buildInitialNotifications(
      eligibleUsers.map((u) => u.id),
      existingNotifications,
    ),
  }

  return (
    <ModalForm
      title="Check-In Alert"
      icon="checkin-alert"
      size="xl"
      onSubmit={onSubmit}
      initialValues={initialValues}
      onClose={handleClose}
      hideActions={!subscription}
    >
      {({ control }) => (
        <>
          <Text mb={2}>
            Notify team members when {description}:
          </Text>
          {previewBox}
          {subscription && (
            <NotificationList
              control={control}
              users={eligibleUsers}
              smsDisabled={!onLargestPlan}
            />
          )}
          {!subscription && (
            <Box mt={4}>
              <UpgradeAlert text="Check-In Alerts are available with a paid subscription." />
            </Box>
          )}
          {subscription && !onLargestPlan && (
            <Box mt={4}>
              <UpgradeAlert
                text="SMS Check-In Alerts are available with an Enterprise subscription."
                defaultSelectedPlan={SubscriptionType.Enterprise}
              />
            </Box>
          )}
        </>
      )}
    </ModalForm>
  )
}

type NotificationListProps = { control: Control<any>; users: User[]; smsDisabled?: boolean }

export const NotificationList = ({ control, users, smsDisabled }: NotificationListProps) => {
  const { field } = useController({ name: "notifications", control })
  const rows: AlertRow[] = field.value

  const update = (userId: string, key: "email" | "sms", value: boolean) => {
    field.onChange(rows.map((r) => (r.userId === userId ? { ...r, [key]: value } : r)))
  }

  if (!users.length) return null

  const rowMap = Object.fromEntries(rows.map((r) => [r.userId, r]))

  return (
    <Box>
      <AlertColumnHeaders />
      <VStack spacing={4} align="stretch">
        {users.map((user) => {
          const row = rowMap[user.id] ?? { userId: user.id, email: false, sms: false }

          return (
            <UserAlertRow
              key={user.id}
              user={user}
              email={row.email}
              sms={row.sms}
              onEmailChange={(v) => update(user.id, "email", v)}
              onSmsChange={(v) => update(user.id, "sms", v)}
              smsDisabled={smsDisabled}
            />
          )
        })}
      </VStack>
    </Box>
  )
}

export default CheckInAlert
